import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface HeaderVisibilityContextType {
  isHeaderVisible: boolean;
  setHeaderVisible: (visible: boolean) => void;
  isAutoHideActive: boolean;
  setAutoHideActive: (active: boolean) => void;
}

const HeaderVisibilityContext = createContext<HeaderVisibilityContextType | undefined>(undefined);

export const useHeaderVisibility = () => {
  const context = useContext(HeaderVisibilityContext);
  if (context === undefined) {
    throw new Error('useHeaderVisibility must be used within a HeaderVisibilityProvider');
  }
  return context;
};

interface HeaderVisibilityProviderProps {
  children: React.ReactNode;
}

export const HeaderVisibilityProvider: React.FC<HeaderVisibilityProviderProps> = ({ children }) => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isAutoHideActive, setIsAutoHideActive] = useState(false);
  const [isMouseInTopArea, setIsMouseInTopArea] = useState(false);

  useEffect(() => {
    // Handle header visibility based on auto-hide and mouse position
    if (isAutoHideActive) {
      // In quiz mode, show header only when mouse is in top 5%
      setIsHeaderVisible(isMouseInTopArea);
    } else {
      // Normal mode, always show header
      setIsHeaderVisible(true);
    }
  }, [isAutoHideActive, isMouseInTopArea]);

  useEffect(() => {
    // Only add mouse listeners when auto-hide is active
    if (!isAutoHideActive) return;

    const handleMouseMove = (event: MouseEvent) => {
      const topThreshold = window.innerHeight * 0.05; // 5% of viewport height
      const isInTopArea = event.clientY <= topThreshold;
      setIsMouseInTopArea(isInTopArea);
    };

    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isAutoHideActive]);

  // Reset header visibility when route changes (only if auto-hide is not active)
  // This was causing the header to show even when auto-hide was active
  // Will implement smarter logic later if needed

  const setHeaderVisible = (visible: boolean) => {
    setIsHeaderVisible(visible);
  };

  const setAutoHideActive = useCallback((active: boolean) => {
    setIsAutoHideActive(active);
    if (!active) {
      setIsHeaderVisible(true);
    }
  }, []);

  return (
    <HeaderVisibilityContext.Provider
      value={{
        isHeaderVisible,
        setHeaderVisible,
        isAutoHideActive,
        setAutoHideActive,
      }}
    >
      {children}
    </HeaderVisibilityContext.Provider>
  );
};